// The wording of the Team drafts page, its nav link and the store hint, as pure functions
// (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md, "Team drafts" and "The
// store hint"). The ages come from tab-model.js's formatAge, so the tab row and these
// agree on what "12m" means.

import { formatAge } from './tab-model.js';

/**
 * "12m ago", "3h ago", "2d ago"; "just now" under a minute (or when the browser's clock is
 * a little ahead of the server's); '' when the time is missing or unreadable.
 *
 * @param {string|null|undefined} iso  an ISO 8601 time
 * @param {number|Date} now            the clock, injected
 */
export function agoText(iso, now) {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const nowMs = now instanceof Date ? now.getTime() : now;
  const age = formatAge(nowMs - then);
  return age === 'now' ? 'just now' : `${age} ago`;
}

/**
 * One line of the store hint: "Pat started a ticket for this store 12m ago". Reads only
 * `display_name` and `started_at`, so nothing else a row carries can reach the screen.
 */
export function hintSentence(row, now) {
  const name = typeof row?.display_name === 'string' ? row.display_name.trim() : '';
  const ago = agoText(row?.started_at, now);
  return `${name || 'Someone'} started a ticket for this store${ago ? ` ${ago}` : ''}`;
}

/**
 * The rows to show for `locationId`, from a stored response `{ locationId, rows }`. A
 * response made for another store, or none yet, or a failed read (`rows` null) shows
 * nothing: the hint is never allowed to describe a store that is no longer the chosen one.
 */
export function hintRowsFor(stored, locationId) {
  if (!stored || locationId == null) return [];
  if (String(stored.locationId) !== String(locationId)) return [];
  return Array.isArray(stored.rows) ? stored.rows : [];
}

/** The take-over confirmation, naming the owner. */
export function takeOverPrompt(owner) {
  const name = typeof owner?.display_name === 'string' ? owner.display_name.trim() : '';
  if (!name) return "Take over this person's draft? It will leave their tabs.";
  return `Take over ${name}'s draft? It will leave ${name}'s tabs.`;
}

/** The store column: "#104 · Acme - Main St", else the store or organization name, else an em dash. */
export function storeText({ location, organization } = {}) {
  const name = location?.name || '';
  if (location?.store_number) return name ? `#${location.store_number} · ${name}` : `#${location.store_number}`;
  return name || organization?.name || '—';
}

/** The nav link's accessible name; the visible badge is a number alone, so say what it counts. */
export function teamLinkName(count) {
  return count > 0 ? `Team drafts, ${count} open` : 'Team drafts';
}
